import type { KeyboardEvent, ReactNode } from 'react';
import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { cx } from '../cx';
import { EmptyState } from '../EmptyState/EmptyState';
import { Skeleton } from '../Skeleton/Skeleton';
import {
  backgroundClass,
  type ChartPoint,
  FALLBACK_WIDTH_PX,
  formatValue,
  NO_DATA,
  type SeriesColor,
} from './chartModel';

/**
 * THE SHELL EVERY CHART SITS IN, AND IT OWNS ITS OWN LAYOUT (`ui-foundation.md` D1,
 * `reports.md` T2 and T3).
 *
 * There is no `className` prop on this component or on any chart that uses it. D1 drew
 * that line for the floating and chrome primitives after a caller set `w-80 p-3` inside a
 * `Popover` that already owned both; a chart is the same kind of thing for a stronger
 * reason - its width is the thing it measures itself against, so a caller who sets one is
 * telling the chart a lie about the box it is drawing into. Size comes from the CONTAINER
 * and from `height`.
 *
 * WHAT THE FRAME IS RESPONSIBLE FOR, so no chart has to be:
 *  - the accessible NAME, and the visible title when there is one;
 *  - measuring the container, so the plot is drawn in real pixels rather than scaled up
 *    from a viewBox - a scaled viewBox would shrink the axis labels below the 12px floor
 *    at 240px, which is the one thing DESIGN.md's first criterion forbids outright;
 *  - the single tab stop, the arrow-key path over the data and the live region that
 *    announces the focused value;
 *  - the visually hidden data table, which is the text alternative and the reason the
 *    drawing itself can be `aria-hidden`;
 *  - empty, loading and refetching, each of which says something different.
 */

/** The numbers, once, in the shape the hidden table renders. */
export interface ChartTable {
  /** One per column after the first, which is always the category. */
  readonly columns: readonly string[];
  readonly rows: readonly { readonly label: string; readonly values: readonly (number | null)[] }[];
  /** The heading over the first column: "Month", "Stage", "Lifecycle". */
  readonly categoryHeading: string;
}

export interface ChartLegendEntry {
  readonly id: string;
  readonly label: string;
  readonly color: SeriesColor;
}

/** Where the arrow keys currently are. `series` is the row, `point` is the column. */
export interface ChartFocus {
  readonly series: number;
  readonly point: number;
}

export interface ChartPlotBox {
  readonly width: number;
  readonly height: number;
  readonly focus: ChartFocus | null;
}

export interface ChartFrameProps {
  /** The accessible name, and the visible title unless `titleHidden` is set. */
  readonly name: string;
  /** Keeps the name for assistive technology while the surrounding card carries it visibly. */
  readonly titleHidden?: boolean | undefined;
  /** The plot's height in pixels. Width always comes from the container. */
  readonly height: number;
  /**
   * `fixed` pins the plot box to `height`, which is what a drawing needs: an SVG has to
   * know the box it scales into. `min` treats it as a floor, for a plot made of real text
   * whose height is whatever its content needs at this width - the funnel at 375, where
   * five stages stack into a column.
   */
  readonly sizing?: 'fixed' | 'min' | undefined;
  readonly loading?: boolean | undefined;
  /** D20: the previous render is HELD at reduced opacity and nothing moves or re-animates. */
  readonly refetching?: boolean | undefined;
  /** The sentence under the "No Data" title, shown in place of the plot. */
  readonly emptyMessage?: string | undefined;
  readonly empty: boolean;
  /**
   * Rendered whenever it is not empty, so WHETHER there is a legend is the chart's call
   * rather than the frame's. A bar chart with one series does not pass one - its own name
   * already says what the bars are - and a donut always does, because there the legend IS
   * the axis and without it a slice has no label anywhere on the screen.
   */
  readonly legend?: readonly ChartLegendEntry[] | undefined;
  /**
   * The text alternative for a chart whose numbers are DRAWN (`reports.md` T3, as amended
   * by the owner on 2026-09-06). A chart that renders its numbers as real text - the
   * funnel - passes none: a hidden table repeating what a screen reader has just read out
   * is not a second chance at the data, it is the same data twice.
   */
  readonly table?: ChartTable | undefined;
  /** How many rows the arrow keys can move through (series), and how many columns. */
  readonly rows: number;
  readonly columns: number;
  /** What the live region says for a focused cell. The chart knows its own wording. */
  readonly announce: (focus: ChartFocus) => string;
  /**
   * `graphic` marks the plot `role="img"`: the drawing is decorative and the hidden table
   * is the content. `content` is for a plot made of real text - the funnel - where hiding
   * the descendants would hide the numbers a sighted reader is looking straight at.
   */
  readonly presentation?: 'graphic' | 'content' | undefined;
  readonly children: (box: ChartPlotBox) => ReactNode;
}

export function ChartFrame({
  name,
  titleHidden = false,
  height,
  sizing = 'fixed',
  loading = false,
  refetching = false,
  emptyMessage = 'Nothing in this range yet.',
  empty,
  legend,
  table,
  rows,
  columns,
  announce,
  presentation = 'graphic',
  children,
}: ChartFrameProps) {
  const captionId = useId();
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH_PX);
  const [focus, setFocus] = useState<ChartFocus | null>(null);

  /**
   * MEASURED BEFORE PAINT, and `useLayoutEffect` is the whole reason: a chart that renders
   * at the fallback width and then snaps to 240px has painted a layout nobody asked for,
   * and at a narrow width that first frame is an SVG wider than its container, which widens
   * the page for one tick. The app is a Vite SPA with no server rendering
   * (`main.tsx` is `createRoot`), so the usual hydration warning cannot apply.
   */
  useLayoutEffect(() => {
    const element = box.current;
    if (element === null) return;
    const read = () => {
      const measured = element.getBoundingClientRect().width;
      if (measured > 0) setWidth(measured);
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const move = useCallback(
    (deltaSeries: number, deltaPoint: number, absolute?: 'first' | 'last') => {
      setFocus((current) => {
        const start = current ?? { series: 0, point: 0 };
        if (absolute !== undefined) {
          return { series: start.series, point: absolute === 'first' ? 0 : columns - 1 };
        }
        return {
          series: Math.min(Math.max(start.series + deltaSeries, 0), Math.max(rows - 1, 0)),
          point: Math.min(Math.max(start.point + deltaPoint, 0), Math.max(columns - 1, 0)),
        };
      });
    },
    [columns, rows],
  );

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (columns === 0) return;
    const keys: Record<string, () => void> = {
      ArrowRight: () => move(0, 1),
      ArrowLeft: () => move(0, -1),
      ArrowDown: () => move(1, 0),
      ArrowUp: () => move(-1, 0),
      Home: () => move(0, 0, 'first'),
      End: () => move(0, 0, 'last'),
    };
    const handler = keys[event.key];
    if (handler === undefined) return;
    // The arrow keys scroll the page by default, and a chart that scrolled the report away
    // while you read it would be worse than no keyboard path at all.
    event.preventDefault();
    handler();
  };

  const plot = (
    <div
      ref={box}
      className={cx(
        'relative w-full min-w-0',
        // D20 again: opacity only. Nothing may move while the numbers are re-read.
        refetching && 'opacity-60 transition-opacity duration-(--duration-base)',
      )}
      style={
        sizing === 'fixed'
          ? { height: `${String(height)}px` }
          : { minHeight: `${String(height)}px` }
      }
    >
      {loading ? (
        <Skeleton shape="block" className="h-full w-full" label="Loading this chart's numbers." />
      ) : empty ? (
        <div className="flex h-full items-center justify-center">
          <EmptyState icon="search" title="No data" description={emptyMessage} />
        </div>
      ) : (
        <FocusablePlot
          presentation={presentation}
          label={`${name}. ${describeShape(rows, columns)} Use the arrow keys to read the values${table === undefined ? '' : ', or the table below this chart'}.`}
          onKeyDown={onKeyDown}
          onBlur={() => setFocus(null)}
        >
          {children({ width, height, focus })}
        </FocusablePlot>
      )}
    </div>
  );

  return (
    /**
     * `w-full`, AND IT IS LOAD-BEARING. Without it the figure is content-sized, and the
     * content whose size it takes is the SVG - which was drawn at whatever width the frame
     * last measured. That closes a loop: the chart reports its own fallback width as its
     * intrinsic size, the container adopts it, the observer measures it back, and the chart
     * settles at 640px inside a 1176px column and calls it responsive. Measured in the
     * gallery at 1440: every chart 640px wide in an 1176px cell. A chart's size comes from
     * its CONTAINER, which is only true if the container's size does not come from it.
     */
    <figure aria-labelledby={captionId} className="flex w-full min-w-0 flex-col gap-2">
      <figcaption
        id={captionId}
        className={cx(titleHidden ? 'sr-only' : 'text-sm font-medium text-ink')}
      >
        {name}
      </figcaption>
      {plot}
      {legend !== undefined && legend.length > 0 && !loading && !empty && (
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {legend.map((entry) => (
            <li key={entry.id} className="flex min-w-0 items-center gap-1.5">
              <span
                aria-hidden="true"
                className={cx('size-2.5 shrink-0 rounded-pill', backgroundClass(entry.color))}
              />
              <span className="min-w-0 truncate text-2xs text-ink-muted">{entry.label}</span>
            </li>
          ))}
        </ul>
      )}
      {/*
          THE VALUE, SPOKEN. A changing `aria-label` on a focused element is not reliably
          announced by any screen reader; a polite live region is. So the arrow keys write
          here, and this sentence is what a keyboard reader hears as they move.
        */}
      <p aria-live="polite" className="sr-only">
        {focus === null || loading || empty ? '' : announce(focus)}
      </p>
      {!loading && table !== undefined && <ChartDataTable name={name} table={table} />}
    </figure>
  );
}

/**
 * THE ONE TAB STOP, WRITTEN OUT TWICE ON PURPOSE.
 *
 * The role differs by presentation and everything else is identical, which would be one
 * element with a computed `role` - except that a computed role is invisible to the linter
 * that checks whether `aria-label` is allowed on it, and to the one that checks whether a
 * key handler sits on something interactive. Both of those rules exist to catch exactly
 * the mistake this component could make, so the duplication buys real checking.
 *
 * `role="img"` marks the SVG charts as one graphic whose content is the hidden table.
 * `role="group"` is for the funnel, whose numbers are real text a screen reader must reach.
 */
function FocusablePlot({
  presentation,
  label,
  onKeyDown,
  onBlur,
  children,
}: {
  readonly presentation: 'graphic' | 'content';
  readonly label: string;
  readonly onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  readonly onBlur: () => void;
  readonly children: ReactNode;
}) {
  const shell = 'h-full w-full rounded-control';
  return presentation === 'graphic' ? (
    <div
      // biome-ignore lint/a11y/noNoninteractiveTabindex: the chart IS the arrow keys' target
      tabIndex={0}
      role="img"
      aria-label={label}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      className={shell}
    >
      {children}
    </div>
  ) : (
    // The semantic element for `role="group"` is <fieldset>, which exists to group FORM
    // CONTROLS under a legend; a funnel's stages are neither.
    // biome-ignore lint/a11y/useSemanticElements: a fieldset groups form controls, not stages
    <div
      // biome-ignore lint/a11y/noNoninteractiveTabindex: the chart IS the arrow keys' target
      tabIndex={0}
      role="group"
      aria-label={label}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      className={shell}
    >
      {children}
    </div>
  );
}

function describeShape(rows: number, columns: number): string {
  const points = `${columns} ${columns === 1 ? 'data point' : 'data points'}`;
  return rows <= 1 ? `Chart of ${points}.` : `Chart of ${rows} series over ${points}.`;
}

/**
 * THE TEXT ALTERNATIVE, and it is a real table rather than a sentence (`reports.md` T3).
 *
 * A table is navigable - row by row, column by column, with the headers announced - and a
 * paragraph listing thirty numbers is not. It carries the FULL labels, including the ones
 * the axis truncated to fit, so nothing a chart drops for space is lost to a reader who
 * cannot see the drawing.
 */
function ChartDataTable({ name, table }: { readonly name: string; readonly table: ChartTable }) {
  /**
   * THE WRAPPER IS NOT DECORATION, and the measure step is what proved it: `sr-only` sets
   * `width: 1px`, and on a `display: table` element width is a MINIMUM, not a size. A seven
   * column table therefore laid itself out at its full 461px, absolutely positioned, and
   * pushed the page 109px sideways at 375 - a visually hidden element making the whole
   * document pan. A plain `<div>` does shrink to 1px and clips what is inside it, so the
   * table keeps its own semantics and stops being part of the visual layer.
   */
  return (
    <div className="sr-only">
      <table>
        <caption>{name}</caption>
        <thead>
          <tr>
            <th scope="col">{table.categoryHeading}</th>
            {table.columns.map((column) => (
              <th key={column} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            // Two categories CAN share a label - two options spelled the same, two buckets a
            // formatter rendered identically - so the row's position is its identity here.
            <tr key={`${row.label}-${String(rowIndex)}`}>
              <th scope="row">{row.label}</th>
              {row.values.map((value, index) => (
                <td key={table.columns[index] ?? String(index)}>
                  {value === null ? NO_DATA : formatValue(value)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A category table from one dimension: the donut's slices, the funnel's stages. */
export function singleColumnTable(
  points: readonly ChartPoint[],
  categoryHeading: string,
  columnHeading: string,
): ChartTable {
  return {
    categoryHeading,
    columns: [columnHeading],
    rows: points.map((point) => ({ label: point.label, values: [point.value] })),
  };
}
